import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@mui/material";
import { ActionButtonText } from "./PaymentDialog-types";
/**
 * This component renders action buttons for the payment dialog.
 * It provides "Cancel" and "Confirm" buttons with dynamic text and state-based
 * enable/disable logic based on the current payment and error states.
 *
 * @param {boolean} [props.loading] - Indicates if a payment process is currently loading.
 *                                    Disables the buttons while loading is in progress.
 * @param {string} [props.error] - An error message indicating if an error occurred.
 *                                 Adjusts the cancel button text and disables the confirm button.
 * @param {boolean} [props.paymentComplete] - Flag indicating whether the payment has been completed.
 *                                            Disables all buttons if payment is complete.
 * @param {Object} [props.giftCard] - The gift card object, if present. Determines if the gift card
 *                                    balance is sufficient to cover the remaining balance.
 * @param {Object} [props.paymentMethod] - The payment method object, if present. Used to determine
 *                                         if a valid payment method is selected.
 * @param {number} [props.remainingBalance] - The remaining balance to be paid. Used to calculate
 *                                            whether the balance can be fully paid.
 *
 * @param {Function} props.onClose - Callback function triggered when the "Cancel" button is clicked.
 *                                   Receives the click event and an optional reason as parameters.
 * @param {Function} props.handleConfirmClick - Callback function triggered when the "Confirm" button is clicked.
 *
 * @returns {JSX.Element} A set of action buttons:
 *                        - "Cancel" button with dynamic text, disabled if loading or payment is complete.
 *                        - "Confirm" button with dynamic text, disabled based on loading, error, payment completion,
 *                          or inability to pay the balance.
 */
export const PaymentDialogActions = ({ loading, error, paymentComplete, myMealsConfig, canBalanceBePaid, onClose, handleConfirmClick, }) => {
    var _a, _b;
    const isConfirmDisabled = loading || error || paymentComplete || !canBalanceBePaid;
    const cancelButtonText = error
        ? ActionButtonText.OK
        : (_a = myMealsConfig.PaymentDialogCancelButtonText) !== null && _a !== void 0 ? _a : ActionButtonText.CANCEL;
    const confirmButtonText = (_b = myMealsConfig.PaymentDialogConfirmButtonText) !== null && _b !== void 0 ? _b : ActionButtonText.CONFIRM;
    return (_jsxs(_Fragment, { children: [_jsx(Button, Object.assign({ disabled: loading || paymentComplete, onClick: (event) => onClose(event, undefined), variant: "outlined", color: "secondary", fullWidth: true }, { children: cancelButtonText })), _jsx(Button, Object.assign({ disabled: isConfirmDisabled, onClick: handleConfirmClick, variant: "contained", color: "primary", fullWidth: true }, { children: confirmButtonText }))] }));
};
